import * as React from 'react';
import * as _ from 'lodash';
import { connect } from 'react-redux';
import { CardElement } from '@stripe/react-stripe-js';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import * as DropdownAction from '../../../actions/userDropdown.actions';
import PaymentHOC from '../../../HOC/Payment';
import * as paymentService from '../../../services/payment.service';
import { isEmpty } from 'lodash';
import {
    notificationDefaultMessage,
    questionErrorMessage
} from '../../../constants/applicationMessage';
import { alertMessage } from '../../../constants/alertMessage';
import { push } from 'connected-react-router';
import NotifyHOC from '../../../HOC/Notifications';
import OrderReview from './OrderReview';
import {
    Audience,
    PanelType,
    Project,
    PROJECT_STATUS,
    TENANT_LEVEL,
    VIEW_MODE,
    PriceType,
    QuestionSummary,
    HealthProblemContext,
    HealthProblemType,
    HealthProblem
} from '@nexxt/common/types';
import * as Action from '../../../actions/project.actions';
import * as projectService from '../../../services/project.service';
import { interpolate } from '../../../utils/string';
import {
    EDIT_NEW_PROJECT,
    LAUNCH_PROJECT,
    MANAGE_ACCOUNTS
} from '../../../constants/routes';
import { AxiosResponse } from 'axios';
import { getAudienceByProjectID } from '../../../services/sample.service';
import { getSortedQuestionByProjectId } from '../../../services/question.service';
import Loader from '../../common/Loader/Loader';
import PaymentOption from './PaymentOption';
import {
    getTenantLevel,
    getAvailableProjects,
    setAvailableProjects
} from '@nexxt/common/firebase/auth/userDetails';
import { PAYMENT_TYPES } from './PaymentOption/PaymentOption';
import PriceBreakdownNotice from './PriceBreakdown/PriceBreakdownNotice';
import { sortHealthProblemByQuestionID } from '../../../utils/project';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PriceDetail from '../../project/tab/PriceModal/PriceDetail';
import { Link } from 'react-router-dom';

interface CheckoutProps {
    stripe: Stripe;
    elements: StripeElements;
    user: any;
    toastNotification: any;
    currentProject: Project;
    questionList: QuestionSummary[];
    packageID: number;
    match: any;
    priceType: any;
    viewMode: VIEW_MODE;
    userDropdownHide: () => void;
    goToRoute: (url: string, state) => void;
    setProject: (newProject: Project) => void;
    getAudienceByProjectID: (projectId: string) => Promise<any>;
    getSortedQuestionByProjectId: (projectId: number) => Promise<any>;
    onLaunch: (
        projectId: number,
        packageID: number,
        codeName: string,
        enableEmail: boolean,
        disablePanel: boolean,
        orderReview: any,
        complete: boolean
    ) => Promise<any>;
    projectHealth: any;
}

interface CheckoutState {
    isLoading: boolean;

    audience: Audience;
    subTotalPrice: number;
    estimatedTax: number;
    totalPrice: number;
    orderReview: any;

    cardProcessing: boolean;
    cardComplete: boolean;
    selectedCard: any;
    billing_info: {
        fullName: string;
        postalCode: string;
        country: string;
    };
    paymentIntentSucceeded: boolean;
    clientKey: string;
    paymentRequired: boolean;

    errorObject: any;
    errorShow: boolean;
    confirmedCheckout: boolean;
}

const DEFAULT_STATE = {
    cardComplete: false,
    cardProcessing: false,
    billing_info: {
        postalCode: '',
        country: 'CA',
        fullName: ''
    },
    errorShow: false,
    clientKey: '',
    errorObject: {},
    paymentIntentSucceeded: false,
    confirmedCheckout: false
};

class Checkout extends React.Component<CheckoutProps, CheckoutState> {
    state: CheckoutState = {
        ...DEFAULT_STATE,
        billing_info: DEFAULT_STATE.billing_info,
        audience: null,
        isLoading: false,
        selectedCard: null,
        subTotalPrice: this.props.priceType?.price, // no tax
        estimatedTax: null,
        totalPrice: null,
        orderReview: {},
        paymentRequired:
            this.props?.priceType?.PRICE_MODE !== PriceType.FREE &&
            this.props.viewMode !== VIEW_MODE.DEV,
        confirmedCheckout: false
    };

    async componentDidMount() {
        const {
            match,
            questionList,
            goToRoute,
            getAudienceByProjectID,
            getSortedQuestionByProjectId
        } = this.props;

        this.setState(
            {
                isLoading: true
            },
            async () => {
                const projectID = match.params.projectId;
                const promises = [await getAudienceByProjectID(projectID)];
                if (_.isEmpty(this.props.currentProject)) {
                    promises.push(await this.fetchProjectById());
                }
                if (_.isEmpty(questionList)) {
                    promises.push(
                        await getSortedQuestionByProjectId(projectID)
                    );
                }
                try {
                    const responses = await Promise.all(promises);

                    this.setState(
                        {
                            audience: responses[0] as Audience,
                            isLoading: false
                        },
                        this.getOrderInformation
                    );
                } catch (e) {
                    console.error(e);
                }
            }
        );
    }
    fetchProjectById = async () => {
        const { match, toastNotification, setProject } = this.props;
        try {
            const projectID = parseInt(match.params.projectId);
            const response: AxiosResponse = await projectService.fetchProject(
                projectID // change this to fetch by another method if has issue in future
            );
            const projectData = {
                ...response.data.data,
                packageID: response.data.packageID
            };

            setProject(projectData);
        } catch (error) {
            toastNotification.error(alertMessage.UNABLE_FETCH_PROJECT);
        }
    };

    getOrderInformation = () => {
        const { audience } = this.state;
        const { currentProject, questionList, priceType, viewMode } =
            this.props;
        const isSelfServiceTenant =
            getTenantLevel() === TENANT_LEVEL.SELF_SERVICE;
        const dayInField = isSelfServiceTenant ? 1 : 7;
        const totalSize = isSelfServiceTenant
            ? audience?.predefinedAudience?.reduce((totalSize, item) => {
                  totalSize += item.totalCount;
                  return totalSize;
              }, 0)
            : audience?.totalSampleSize;

        const estimatedDate = new Date();
        estimatedDate.setDate(estimatedDate.getDate() + dayInField);
        const estimatedDateStr = estimatedDate.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
        const targetingOptions = audience?.builtInAudienceRules?.map(
            (builtIn) => {
                const options = builtIn.audienceRuleOptions
                    .map((option) => {
                        if (!option.mode.terminateEnabled) {
                            return option.label;
                        }
                        return null;
                    })
                    .filter((opt) => opt);
                return { label: builtIn.selectedQuestion.label, options };
            }
        );

        const screeningOptions = isSelfServiceTenant
            ? audience?.predefinedAudience?.map((audience) => audience.label)
            : audience?.customAudienceRules?.map(
                  (custom) => custom.selectedQuestion.label
              );
        const audiencePrice = priceType?.price;
        const paymentRequired =
            priceType?.PRICE_MODE !== PriceType.FREE &&
            viewMode !== VIEW_MODE.DEV;
        const orderReview = {
            totalPrice: audiencePrice,
            totalSize: totalSize || 0,
            targetingCountries: currentProject?.countries?.map((c) => c.label),
            targetingOptions,
            screeningOptions,
            languages: currentProject?.languages?.map((l) => l.label),
            numOfQuestions: questionList?.length,
            estimatedDateStr
        };

        this.setState({
            orderReview,
            subTotalPrice: audiencePrice,
            paymentRequired
        });
    };

    getPaymentIntent = async () => {
        const { subTotalPrice, billing_info } = this.state;
        const { currentProject } = this.props;
        const amountToInteger = subTotalPrice * 100; // i.e. $10 -> 1000 cents
        // try {
        const clientKey = paymentService
            .getPaymentIntent({
                totalAmount: amountToInteger,
                projectID: currentProject.id,
                billing_details: {
                    email: this.props.user.email,
                    name: billing_info.fullName,
                    address: {
                        country: billing_info.country,
                        postal_code: billing_info.postalCode
                    }
                }
            })
            .then((resp) => {
                const {
                    subTotalPrice: newSubTotal,
                    totalPrice,
                    estimatedTax,
                    clientSecret
                } = resp.data;

                this.setState({
                    subTotalPrice: newSubTotal / 100,
                    totalPrice: totalPrice / 100,
                    estimatedTax: estimatedTax / 100
                });
                return clientSecret;
            })
            .catch((err) => {
                this.props.toastNotification.error(alertMessage.PAYMENT_ERROR);
                return null;
            });

        return clientKey;
        // } catch (err) {
        //     this.props.toastNotification.error(
        //         notificationDefaultMessage.ERROR
        //     );
        //     return null;
        // }
    };

    resetCardForm = () => {
        const { elements } = this.props;

        elements?.getElement(CardElement)?.clear();
        this.setState(DEFAULT_STATE);
    };

    onChangeInfo = (keyName, value) => {
        this.setState({
            ...this.state,
            billing_info: {
                ...this.state.billing_info,
                [keyName]: value
            }
        });
    };

    onCardChange = (event) => {
        this.setState((prevState) => ({
            errorObject: {
                ...prevState.errorObject,
                card: event?.error?.message
            },
            cardComplete: event.complete,
            billing_info: {
                ...prevState.billing_info,
                postalCode: event.value?.postalCode
            }
        }));
    };

    onSelectCard = (card) => {
        this.setState({ selectedCard: card });
    };

    validate = () => {
        const { billing_info } = this.state;
        const newErrorObject = {
            fullName: '',
            postalCode: '',
            country: '',
            card: ''
        };
        let isError = false;
        if (isEmpty(billing_info.fullName)) {
            newErrorObject.fullName = questionErrorMessage.REQUIRED;
            isError = true;
        }
        if (isEmpty(billing_info.postalCode)) {
            newErrorObject.postalCode = questionErrorMessage.REQUIRED;
            isError = true;
        }
        if (isEmpty(billing_info.country)) {
            newErrorObject.country = questionErrorMessage.REQUIRED;
            isError = true;
        }
        this.setState({
            errorObject: { ...this.state.errorObject, ...newErrorObject }
        });
        return { errorObject: newErrorObject, isError };
    };

    handlePaymentIntent = async () => {
        const { stripe, elements, toastNotification } = this.props;
        const { cardComplete } = this.state;

        if (!stripe || !elements) {
            toastNotification.error(alertMessage.CHECKOUT_STRIPE_FAIL);
            return;
        }

        const billingError = this.validate();

        if (billingError.isError) {
            if (billingError.errorObject.card) {
                elements.getElement('card').focus();
                return;
            }
            this.setState({
                errorShow: true,
                errorObject: billingError.errorObject
            });
            toastNotification.warning(alertMessage.CHECKOUT_FAIL);
            return;
        }

        if (cardComplete) {
            this.setState({ cardProcessing: true });
        }

        const clientKey = await this.getPaymentIntent();
        this.setState({
            clientKey,
            cardProcessing: false,
            paymentIntentSucceeded: clientKey ? true : false
        });
    };

    launchProject = async () => {
        const {
            currentProject,
            packageID,
            stripe,
            elements,
            toastNotification,
            goToRoute
        } = this.props;
        const {
            orderReview,
            billing_info,
            clientKey,
            selectedCard,
            paymentRequired
        } = this.state;
        this.setState({ isLoading: true });

        const disablePanel =
            currentProject.panelSettings?.panelType !== PanelType.INCA;
        let validToCheckout = true;
        if (paymentRequired) {
            if (selectedCard?.type === PAYMENT_TYPES.CREDIT_CARD) {
                const cardElement = elements.getElement(CardElement);
                await stripe
                    .confirmCardPayment(clientKey, {
                        payment_method: {
                            card: cardElement,
                            billing_details: {
                                email: this.props.user.email,
                                name: billing_info.fullName,
                                address: {
                                    country: billing_info.country,
                                    postal_code: billing_info.postalCode
                                }
                            }
                        }
                    })
                    .then((response) => {
                        if (response.paymentIntent.status === 'succeeded') {
                            toastNotification.success(
                                alertMessage.SUCCESS_PAYMENT
                            );
                        } else {
                            validToCheckout = false;
                        }
                    })
                    .catch((err) => {
                        this.setState({
                            errorObject: {
                                ...this.state.errorObject,
                                payment: err.error
                            }
                        });
                        this.setState({ isLoading: false });
                        validToCheckout = false;

                        toastNotification.error(
                            err.error || notificationDefaultMessage.ERROR
                        );
                    });
                if (!validToCheckout) {
                    return;
                }
            }
        }
        validToCheckout &&
            (await this.props
                .onLaunch(
                    currentProject?.id,
                    packageID,
                    currentProject?.codeName,
                    process.env.NODE_ENV == 'production',
                    disablePanel,
                    {
                        ...orderReview,
                        paymentMethod: selectedCard?.type,
                        totalPrice:
                            this.state.totalPrice || this.state.subTotalPrice
                    },
                    currentProject.status == PROJECT_STATUS.COMPLETE
                )
                .then((response) => {
                    setAvailableProjects(response.data.availableProjects);
                    this.setState(
                        {
                            isLoading: false
                        },
                        () => {
                            goToRoute(
                                interpolate(LAUNCH_PROJECT, {
                                    projectId: currentProject.id
                                }),
                                { purchaseID: response.data.purchaseID }
                            );
                        }
                    );
                })
                .catch((err) => {
                    console.error(err);
                    toastNotification.error(
                        `${
                            err?.response?.data?.message ||
                            notificationDefaultMessage.ERROR
                        }`
                    );
                    this.setState({ isLoading: false });
                }));
    };

    render() {
        const { currentProject, priceType, projectHealth } = this.props;
        const {
            isLoading,
            orderReview,
            billing_info,
            errorObject,
            errorShow,
            paymentIntentSucceeded,
            cardProcessing,
            selectedCard,
            paymentRequired,
            confirmedCheckout
        } = this.state;
        const availableProjects = getAvailableProjects();

        const projectHealthWarnings = Object.values(projectHealth).flatMap(
            (questionProblem) =>
                (questionProblem as any).list.map(
                    (problem) => problem as HealthProblem
                )
        );

        const isLive = this.props.currentProject.status == PROJECT_STATUS.LIVE;
        const customPricing =
            ![PriceType.REGULAR, PriceType.FREE].includes(
                priceType.PRICE_MODE
            ) && !priceType.price;
        const baseItems = [
            {
                include: !isLive,
                message: `Your live link will be "https://demo.nexxt.in/p/${
                    this.props?.currentProject?.codeName
                }".${
                    this.props?.currentProject?.codeName?.length > 10
                        ? ' We recommend shorter project urls for easy sharing.'
                        : ''
                } If you would like to update your project link, you can do so on the Overview page. This cannot be updated after launching your project`
            },
            {
                include:
                    !isLive &&
                    this.props.currentProject?.panelSettings?.panels?.filter(
                        (panel) => panel.name !== 'General'
                    )?.length > 0,
                message:
                    'Please make sure to communicate with your panel provider to ensure all your redirects are properly being captured and that the study link is updated once the live link has been generated after checkout'
            },
            {
                include: customPricing,
                problemType: HealthProblemType.ERROR,
                message:
                    'Incidence rate must be set on the audience page for projects with custom screeners to better estimate final checkout costs'
            }
        ].filter((item) => item.include);

        const prelaunchItems = [...baseItems, ...projectHealthWarnings];

        const hasProjectHealthErrors = prelaunchItems.filter(
            (item) => item.problemType == HealthProblemType.ERROR
        ).length;

        const readyCheckout =
            (confirmedCheckout &&
                (isLive ||
                    (selectedCard &&
                        (paymentIntentSucceeded ||
                            selectedCard.type === PAYMENT_TYPES.INVOICE))) &&
                !customPricing &&
                !hasProjectHealthErrors) ||
            this.props.viewMode == VIEW_MODE.DEV;
        return (
            <div className="w-full">
                {isLoading && <Loader isFixed />}
                <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-4">
                        <div className="checkout--container--column-body h-full w-full flex flex-col">
                            <p className="checkout--container--column-title">
                                Pre-launch Checklist
                            </p>
                            {prelaunchItems?.length > 0 ? (
                                <p>
                                    Below is a list of suggestions or items to
                                    be addressed before launching your project
                                </p>
                            ) : (
                                <p>
                                    No suggestions here! Your project looks
                                    ready to go!
                                </p>
                            )}
                            <ol className="list-decimal ml-3 flex-1">
                                {prelaunchItems.map((warning, k) => (
                                    <li
                                        className={`${
                                            warning.problemType ==
                                            HealthProblemType.ERROR
                                                ? 'text-red'
                                                : ''
                                        }`}
                                        key={k}
                                    >
                                        {warning.message}
                                    </li>
                                ))}
                            </ol>
                            <div>
                                {hasProjectHealthErrors ? (
                                    <p className="text-red">
                                        <span className="font-semibold">
                                            Error:
                                        </span>
                                        {` `}
                                        You must clear all errors on your
                                        project before launching your project.
                                        If you are having any troubles with
                                        this, please contact our suppor team for
                                        assistance.
                                    </p>
                                ) : null}
                                {currentProject.status ==
                                PROJECT_STATUS.LIVE ? (
                                    <p>
                                        <span className="font-semibold">
                                            Note:
                                        </span>
                                        {` `}
                                        When republishing projects, it may take
                                        a few minutes to update on your
                                        dashboard.
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <div className="col-span-4 checkout--container--column-body flex flex-col">
                        <PriceDetail
                            isPriceModal={false}
                            type={{
                                ...priceType,
                                price: this.state.subTotalPrice,
                                estimatedTax: this.state.estimatedTax,
                                totalPrice: this.state.totalPrice
                            }}
                            totalQuestionCount={this.props.questionList?.length}
                            currentProject={currentProject}
                            orderReview={orderReview}
                        />
                    </div>
                    <div className="flex flex-col gap-4 col-span-4">
                        <div className="checkout--container--column-body h-full flex flex-col">
                            <p className="checkout--container--column-title">
                                Payment Confirmation
                            </p>
                            {availableProjects ? (
                                <div>
                                    <PaymentOption
                                        billing_info={billing_info}
                                        billing_info_error={errorObject}
                                        errorShow={errorShow}
                                        processing={cardProcessing}
                                        onChangeInfo={this.onChangeInfo}
                                        onCardChange={this.onCardChange}
                                        handleSubmit={this.handlePaymentIntent}
                                        onSelectCard={this.onSelectCard}
                                        selectedCard={selectedCard}
                                        paymentIntentSucceeded={
                                            paymentIntentSucceeded
                                        }
                                        paymentRequired={availableProjects}
                                        // paymentRequired={paymentRequired}
                                        onReset={this.resetCardForm}
                                    />
                                </div>
                            ) : null}
                            <div className="w-full flex flex-col gap-2 mt-2 mb-4">
                                {/* <Link
                                    to={MANAGE_ACCOUNTS}
                                    className="btn btn--sm btn--blue w-full border-none"
                                >
                                    Manage Remaining Credits
                                </Link> */}
                                {!availableProjects
                                    ? 'It looks like you do not have any more remaining project credits. Please contact us to add more credits to your account to launch your project.'
                                    : ''}
                                <a
                                    href={
                                        'mailto:letschat@nexxt.in?subject=Credit Top-Up Request'
                                    }
                                    className="btn btn--sm btn--blue w-full border-none"
                                >
                                    Contact us to Add Credits
                                </a>
                            </div>
                            <div className="flex flex-col gap-2 mt-auto">
                                <div className="flex items-start gap-2">
                                    <input
                                        className="mt-1.5"
                                        type="checkbox"
                                        onChange={(e) =>
                                            this.setState({
                                                confirmedCheckout:
                                                    e.target.checked
                                            })
                                        }
                                        checked={confirmedCheckout}
                                    />
                                    <span>
                                        I agree to the usage of the stated
                                        credits required to launch this project
                                        and to be charged for any additional
                                        fees incurred with the selected payment
                                        option below.
                                    </span>
                                </div>
                                <button
                                    className="btn btn--lg btn--full btn--green"
                                    onClick={this.launchProject}
                                    disabled={!readyCheckout}
                                >
                                    <span className="mr-2">
                                        <FontAwesomeIcon icon={faLock} />
                                    </span>
                                    {currentProject.status ==
                                    PROJECT_STATUS.LIVE
                                        ? 'Republish'
                                        : selectedCard?.type === 'CREDIT_CARD'
                                        ? 'Submit Payment'
                                        : 'Confirm & Launch'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mt-4">
                    <PriceBreakdownNotice />
                </div>
            </div>
        );
    }
}
const mapStateToProps = (store) => {
    const {
        authReducer: { user },
        projectReducer: { currentProject, packageID, priceType, projectHealth },
        questionReducer: { questionSummaries },
        utilsReducer: { viewMode }
    } = store;
    return {
        user,
        currentProject,
        projectHealth: sortHealthProblemByQuestionID(
            projectHealth,
            [
                HealthProblemContext.CONDITION,
                HealthProblemContext.QUESTION,
                HealthProblemContext.AUDIENCE,
                HealthProblemContext.OVERVIEW
            ],
            [HealthProblemType.ERROR, HealthProblemType.WARNING]
        ),
        priceType,
        packageID,
        questionList: questionSummaries[currentProject?.id] || [],
        viewMode
    };
};

const mapDispatchToProps = (dispatch) => ({
    setProject: (newProject: Project) => {
        dispatch(Action.setProject(newProject));
    },
    userDropdownHide: () => {
        dispatch(DropdownAction.userDropdownHide());
    },
    getAudienceByProjectID: (projectId: string) =>
        dispatch(getAudienceByProjectID(projectId)),
    getSortedQuestionByProjectId: (projectId: number) =>
        dispatch(getSortedQuestionByProjectId(projectId)),
    goToRoute: (url: string, state) => dispatch(push(url, state)),
    onLaunch: (
        projectId: number,
        packageID: number,
        codeName: string,
        enableEmail: boolean,
        disablePanel: boolean,
        orderReview: any,
        complete: boolean
    ) =>
        dispatch(
            projectService.onLaunch(
                projectId,
                packageID,
                codeName,
                enableEmail,
                disablePanel,
                orderReview,
                complete
            )
        )
});

export default NotifyHOC(
    PaymentHOC(connect(mapStateToProps, mapDispatchToProps)(Checkout))
);

/**
 * Reference: https://github.com/stripe/react-stripe-js/blob/master/examples/class-components/1-Card-Detailed.js
 */
